import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';
import { Button } from '../../ui/button';
import { Label } from '../../ui/label';
import { Textarea } from '../../ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../../ui/dialog';

interface NameSuggestionsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The list as it stands, one name per line. */
  text: string;
  onSave: (text: string) => Promise<void>;
  /** How many are left, which is the number worth knowing at a glance. */
  count: number;
}

/**
 * The gym's queue of climb names, edited from the field that spends them.
 *
 * The same list the scans page keeps (`climb_name_suggestions` on the gym) and
 * the same shape of editing — a box, one name per line — put behind a button
 * because on this page it is not the work: the setter is naming one problem and
 * only opens the list when it has run dry.
 */
export function NameSuggestionsDialog({
  open,
  onOpenChange,
  text,
  onSave,
  count,
}: NameSuggestionsDialogProps) {
  const { t } = useTranslation();
  const [draft, setDraft] = useState(text);
  const [saving, setSaving] = useState(false);

  // Opening shows the list as it is now — names have been spent since it was
  // last looked at, and it is the current one that is being added to.
  useEffect(() => {
    if (open) setDraft(text);
  }, [open, text]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave(draft);
      onOpenChange(false);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t('climbScan.nameSuggestionsTitle')}</DialogTitle>
          <DialogDescription>
            {t('climbScan.nameSuggestionsRemaining', { count })}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-1.5">
          <Label htmlFor="climb-name-suggestions">{t('gymSettings.climbNameSuggestions')}</Label>
          <Textarea
            id="climb-name-suggestions"
            rows={8}
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            placeholder={t('gymSettings.climbNameSuggestionsPlaceholder')}
          />
          <p className="text-xs text-muted-foreground">
            {t('gymSettings.climbNameSuggestionsHelper')}
          </p>
        </div>

        <Button type="button" onClick={handleSave} disabled={saving}>
          {saving && <Loader2 className="mr-2 size-4 animate-spin" aria-hidden />}
          {t('climbScan.saveSuggestions')}
        </Button>
      </DialogContent>
    </Dialog>
  );
}
